/**
 * Report Builder Modal
 *
 * Step-by-step visual interface for creating custom reports.
 * Guides users through selecting objects, fields, filters, grouping, and chart types.
 */

import React, { useState, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Save, Database, Filter as FilterIcon, BarChart3, Eye } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useObjects } from '../../services/queries/objectQueries';
import { useFieldsByObject } from '../../services/queries/fieldQueries';
import type { ReportConfiguration, ReportFilter, ReportSort, ReportAggregation } from '../../services/reportEngine';
import { executeReport } from '../../services/reportEngine';
import { persistedReportRecords } from '../../services/mockData/reports';
import { persistedReportConfigurations } from '../../services/mockData/reportConfigurations';
import { useToast } from '../../contexts/ToastContext';
import { createRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { getRecordsForReportObject } from '../../hooks/useReportExecution';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';

interface ReportBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingReport?: ReportConfiguration;
}

type BuilderStep = 'object' | 'fields' | 'filters' | 'grouping' | 'sort' | 'chart' | 'preview';

export const ReportBuilderModal: React.FC<ReportBuilderModalProps> = ({
  isOpen,
  onClose,
  existingReport
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  // Current step in the builder
  const [currentStep, setCurrentStep] = useState<BuilderStep>('object');

  // Report configuration state
  const [reportName, setReportName] = useState(existingReport?.name || '');
  const [selectedObject, setSelectedObject] = useState(existingReport?.objectName || '');
  const [selectedFields, setSelectedFields] = useState<string[]>(existingReport?.selectedFields || []);
  const [filters, setFilters] = useState<ReportFilter[]>(existingReport?.filters || []);
  const [groupBy, setGroupBy] = useState<string[]>(existingReport?.groupBy || []);
  const [aggregations, setAggregations] = useState<ReportAggregation[]>(existingReport?.aggregations || []);
  const [sortBy, setSortBy] = useState<ReportSort[]>(existingReport?.sortBy || []);
  const [chartType, setChartType] = useState<'table' | 'bar' | 'line' | 'pie' | 'summary'>(
    existingReport?.chartType || 'table'
  );

  // Preview state
  const [previewResults, setPreviewResults] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Fetch available objects
  const { data: objects = [] } = useObjects();

  // Fetch fields for selected object
  const objectIdMap: Record<string, string> = {
    'Lead': 'obj_lead',
    'Client': 'obj_client',
    'Contact': 'obj_contact',
    'Opportunity': 'obj_opportunity',
    'Case': 'obj_case',
    'Event': 'obj_event',
    'Task': 'obj_task'
  };
  const objectId = objectIdMap[selectedObject] || '';
  const { data: availableFields = [] } = useFieldsByObject(objectId, { includeInactive: false });

  // Step navigation
  const steps: { id: BuilderStep; label: string; icon: React.ReactNode }[] = [
    { id: 'object', label: 'Select Object', icon: <Database className="w-4 h-4" /> },
    { id: 'fields', label: 'Choose Fields', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'filters', label: 'Add Filters', icon: <FilterIcon className="w-4 h-4" /> },
    { id: 'grouping', label: 'Group & Aggregate', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'sort', label: 'Sort', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'chart', label: 'Chart Type', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'preview', label: 'Preview & Save', icon: <Eye className="w-4 h-4" /> }
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  // Navigation handlers
  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const handleStepClick = (stepId: BuilderStep) => {
    // Only allow navigation if object is selected
    if (stepId !== 'object' && !selectedObject) return;
    setCurrentStep(stepId);
  };

  // Can proceed to next step?
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 'object':
        return !!selectedObject;
      case 'fields':
        return selectedFields.length > 0;
      case 'filters':
      case 'grouping':
      case 'sort':
      case 'chart':
        return true;
      case 'preview':
        return reportName.trim().length > 0;
      default:
        return false;
    }
  }, [currentStep, selectedObject, selectedFields, reportName]);

  // Handle preview
  const handlePreview = async () => {
    if (!selectedObject || selectedFields.length === 0) return;

    setIsLoadingPreview(true);
    try {
      const config: ReportConfiguration = {
        id: existingReport?.id || `report_${Date.now()}`,
        name: reportName || 'Untitled Report',
        objectName: selectedObject,
        selectedFields,
        filters,
        groupBy,
        aggregations,
        sortBy,
        chartType
      };

      const records = await getRecordsForReportObject(selectedObject);
      const normalized = records.map(r => normalizeRecordForListView(selectedObject, r));
      const results = await executeReport(config, normalized);
      setPreviewResults(results);
    } catch (err) {
      console.error('Preview error:', err);
      showError('Preview failed', 'Unable to generate report preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Auto-preview when on preview step
  React.useEffect(() => {
    if (currentStep === 'preview' && !previewResults) {
      handlePreview();
    }
  }, [currentStep]);

  const handleSave = async () => {
    if (!reportName.trim() || !selectedObject || selectedFields.length === 0) {
      showError('Validation Error', 'Please provide a report name and select at least one field');
      return;
    }

    const reportId = existingReport?.id || `report_${Date.now()}`;
    const newReport = {
      id: reportId,
      name: reportName,
      description: `Custom report on ${selectedObject}`,
      objectName: selectedObject,
      type: 'Custom' as const,
      format: chartType === 'table' ? 'Table' as const : 'Chart' as const,
      frequency: 'One-time' as const,
      status: 'Active' as const,
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString(),
      isPublic: false
    };
    const reportConfig: ReportConfiguration = {
      id: reportId,
      name: reportName,
      objectName: selectedObject,
      selectedFields,
      filters,
      groupBy,
      aggregations,
      sortBy,
      chartType
    };

    try {
      if (shouldUseGobodo()) {
        const gobodoData: Record<string, any> = {
          Name__c: reportName,
          ObjectName__c: selectedObject,
          Category__c: 'Custom',
          ReportType__c: chartType === 'table' ? 'Tabular' : 'Summary',
          Type__c: 'Custom',
          Format__c: chartType === 'table' ? 'Table' : 'Chart',
          Frequency__c: 'One-time',
          Status__c: 'Active',
          Description__c: newReport.description,
          Owner__c: 'Current User'
        };
        const created = await createRecord('Report__c', gobodoData);
        reportConfig.id = created.id;
        persistedReportConfigurations.add(reportConfig);
        queryClient.invalidateQueries({ queryKey: ['reportListViewData'] });
        queryClient.invalidateQueries({ queryKey: ['reportListViews'] });
        queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Report__c'] });
      } else {
        persistedReportRecords.add(newReport);
        persistedReportConfigurations.add(reportConfig);
        queryClient.invalidateQueries({ queryKey: ['reportListViewData'] });
        queryClient.invalidateQueries({ queryKey: ['reportListViews'] });
      }
      success('Report created', `${reportName} has been saved`);
      onClose();
    } catch (err) {
      console.error('Save report error:', err);
      showError('Save failed', err instanceof Error ? err.message : 'Failed to save report');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white dark:bg-[#1e293b] rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {existingReport ? 'Edit Report' : 'Create New Report'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {steps[currentStepIndex].label}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={step.id !== 'object' && !selectedObject}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      currentStep === step.id
                        ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400'
                        : index < currentStepIndex
                        ? 'text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        : 'text-gray-400 dark:text-gray-500'
                    } ${step.id !== 'object' && !selectedObject ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {step.icon}
                    <span className="text-sm font-medium hidden lg:inline">{step.label}</span>
                  </button>
                  {index < steps.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-600" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {currentStep === 'object' && (
              <ObjectSelectionStep
                selectedObject={selectedObject}
                onSelectObject={setSelectedObject}
                objects={objects}
              />
            )}

            {currentStep === 'fields' && (
              <FieldSelectionStep
                selectedFields={selectedFields}
                onSelectFields={setSelectedFields}
                availableFields={availableFields}
              />
            )}

            {currentStep === 'filters' && (
              <FilterBuilderStep
                filters={filters}
                onUpdateFilters={setFilters}
                availableFields={availableFields}
              />
            )}

            {currentStep === 'grouping' && (
              <GroupingStep
                groupBy={groupBy}
                aggregations={aggregations}
                onUpdateGroupBy={setGroupBy}
                onUpdateAggregations={setAggregations}
                availableFields={availableFields}
              />
            )}

            {currentStep === 'sort' && (
              <SortStep
                sortBy={sortBy}
                onUpdateSortBy={setSortBy}
                availableFields={availableFields}
              />
            )}

            {currentStep === 'chart' && (
              <ChartTypeStep
                chartType={chartType}
                onSelectChartType={setChartType}
              />
            )}

            {currentStep === 'preview' && (
              <PreviewStep
                reportName={reportName}
                onUpdateReportName={setReportName}
                previewResults={previewResults}
                isLoading={isLoadingPreview}
                onRefresh={handlePreview}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handlePrevious}
              disabled={currentStepIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <div className="flex items-center gap-3">
              {currentStep === 'preview' ? (
                <>
                  <button
                    onClick={handlePreview}
                    disabled={isLoadingPreview}
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    Refresh Preview
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={!canProceed}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    Save Report
                  </button>
                </>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!canProceed}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================
// STEP COMPONENTS
// ============================================

/**
 * Step 1: Object Selection
 */
const ObjectSelectionStep: React.FC<{
  selectedObject: string;
  onSelectObject: (object: string) => void;
  objects: any[];
}> = ({ selectedObject, onSelectObject, objects }) => {
  const reportableObjects = [
    { name: 'Lead', icon: '👤', description: 'Potential customers and prospects' },
    { name: 'Client', icon: '🏢', description: 'Active customers and accounts' },
    { name: 'Contact', icon: '👥', description: 'Individual contacts' },
    { name: 'Opportunity', icon: '💰', description: 'Sales opportunities and deals' },
    { name: 'Case', icon: '🎫', description: 'Customer support cases' },
    { name: 'Event', icon: '📅', description: 'Events and activities' },
    { name: 'Task', icon: '✓', description: 'Tasks and to-dos' }
  ];

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Select an object to report on
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose the type of data you want to analyze in your report
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reportableObjects.map((obj) => (
          <button
            key={obj.name}
            onClick={() => onSelectObject(obj.name)}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              selectedObject === obj.name
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{obj.icon}</span>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{obj.name}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{obj.description}</div>
              </div>
              {selectedObject === obj.name && (
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Step 2: Field Selection
 */
const FieldSelectionStep: React.FC<{
  selectedFields: string[];
  onSelectFields: (fields: string[]) => void;
  availableFields: any[];
}> = ({ selectedFields, onSelectFields, availableFields }) => {
  const toggleField = (fieldName: string) => {
    if (selectedFields.includes(fieldName)) {
      onSelectFields(selectedFields.filter(f => f !== fieldName));
    } else {
      onSelectFields([...selectedFields, fieldName]);
    }
  };

  const selectAll = () => {
    onSelectFields(availableFields.map(f => f.fieldName));
  };

  const clearAll = () => {
    onSelectFields([]);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Select fields to include
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose which fields to display in your report ({selectedFields.length} selected)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={selectAll}
            className="px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded transition-colors"
          >
            Select All
          </button>
          <button
            onClick={clearAll}
            className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
        {availableFields.map((field) => (
          <label
            key={field.fieldName}
            className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              checked={selectedFields.includes(field.fieldName)}
              onChange={() => toggleField(field.fieldName)}
              className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900 dark:text-white text-sm">
                {field.fieldLabel}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {field.fieldType} {field.description && `• ${field.description}`}
              </div>
            </div>
          </label>
        ))}
      </div>

      {availableFields.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p>No fields available for this object</p>
        </div>
      )}
    </div>
  );
};

/**
 * Step 3: Filter Builder
 */
const FilterBuilderStep: React.FC<{
  filters: ReportFilter[];
  onUpdateFilters: (filters: ReportFilter[]) => void;
  availableFields: any[];
}> = ({ filters, onUpdateFilters, availableFields }) => {
  const addFilter = () => {
    onUpdateFilters([
      ...filters,
      { field: availableFields[0]?.fieldName || '', operator: 'equals', value: '' }
    ]);
  };

  const updateFilter = (index: number, updates: Partial<ReportFilter>) => {
    const updated = [...filters];
    updated[index] = { ...updated[index], ...updates };
    onUpdateFilters(updated);
  };

  const removeFilter = (index: number) => {
    onUpdateFilters(filters.filter((_, i) => i !== index));
  };

  const operatorsByType: Record<string, { value: string; label: string }[]> = {
    text: [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'startsWith', label: 'Starts With' }
    ],
    number: [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'greaterThan', label: 'Greater Than' },
      { value: 'lessThan', label: 'Less Than' },
      { value: 'between', label: 'Between' }
    ],
    default: [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' }
    ]
  };

  const getOperatorsForField = (fieldName: string) => {
    const field = availableFields.find(f => f.fieldName === fieldName);
    if (!field) return operatorsByType.default;

    if (['number', 'currency', 'percent'].includes(field.fieldType)) {
      return operatorsByType.number;
    } else if (['text', 'textarea', 'email'].includes(field.fieldType)) {
      return operatorsByType.text;
    }
    return operatorsByType.default;
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Add filters (optional)
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Filter records to only include data that meets your criteria
        </p>
      </div>

      <div className="space-y-3">
        {filters.map((filter, index) => (
          <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <select
              value={filter.field}
              onChange={(e) => updateFilter(index, { field: e.target.value })}
              className="flex-1 px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
            >
              {availableFields.map(field => (
                <option key={field.fieldName} value={field.fieldName}>
                  {field.fieldLabel}
                </option>
              ))}
            </select>

            <select
              value={filter.operator}
              onChange={(e) => updateFilter(index, { operator: e.target.value as any })}
              className="px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
            >
              {getOperatorsForField(filter.field).map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>

            <input
              type="text"
              value={filter.value}
              onChange={(e) => updateFilter(index, { value: e.target.value })}
              placeholder="Value"
              className="flex-1 px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
            />

            <button
              onClick={() => removeFilter(index)}
              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={addFilter}
        disabled={availableFields.length === 0}
        className="flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors disabled:opacity-50"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Filter
      </button>

      {filters.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <FilterIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No filters added yet</p>
          <p className="text-sm mt-1">Click "Add Filter" to filter your report data</p>
        </div>
      )}
    </div>
  );
};

/**
 * Step 4: Grouping & Aggregations
 */
const GroupingStep: React.FC<{
  groupBy: string[];
  aggregations: ReportAggregation[];
  onUpdateGroupBy: (groupBy: string[]) => void;
  onUpdateAggregations: (aggs: ReportAggregation[]) => void;
  availableFields: any[];
}> = ({ groupBy, aggregations, onUpdateGroupBy, onUpdateAggregations, availableFields }) => {
  const addGroupBy = (fieldName: string) => {
    if (!groupBy.includes(fieldName)) {
      onUpdateGroupBy([...groupBy, fieldName]);
    }
  };

  const removeGroupBy = (fieldName: string) => {
    onUpdateGroupBy(groupBy.filter(f => f !== fieldName));
  };

  const addAggregation = () => {
    const numericFields = availableFields.filter(f =>
      ['number', 'currency', 'percent'].includes(f.fieldType)
    );
    onUpdateAggregations([
      ...aggregations,
      { field: numericFields[0]?.fieldName || 'id', function: 'count', label: 'Count' }
    ]);
  };

  const updateAggregation = (index: number, updates: Partial<ReportAggregation>) => {
    const updated = [...aggregations];
    updated[index] = { ...updated[index], ...updates };
    onUpdateAggregations(updated);
  };

  const removeAggregation = (index: number) => {
    onUpdateAggregations(aggregations.filter((_, i) => i !== index));
  };

  const numericFields = availableFields.filter(f =>
    ['number', 'currency', 'percent'].includes(f.fieldType)
  );

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Group and aggregate data (optional)
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Group records and calculate summary statistics
        </p>
      </div>

      {/* Group By */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Group By</h4>
        <div className="flex flex-wrap gap-2 mb-3">
          {groupBy.map(field => {
            const fieldDef = availableFields.find(f => f.fieldName === field);
            return (
              <span
                key={field}
                className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 rounded-full text-sm"
              >
                {fieldDef?.fieldLabel || field}
                <button onClick={() => removeGroupBy(field)} className="hover:text-blue-900 dark:hover:text-blue-300">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
        <select
          onChange={(e) => {
            if (e.target.value) {
              addGroupBy(e.target.value);
              e.target.value = '';
            }
          }}
          className="w-full px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
        >
          <option value="">Select field to group by...</option>
          {availableFields
            .filter(f => !groupBy.includes(f.fieldName))
            .map(field => (
              <option key={field.fieldName} value={field.fieldName}>
                {field.fieldLabel}
              </option>
            ))}
        </select>
      </div>

      {/* Aggregations */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Aggregations</h4>
        <div className="space-y-3 mb-3">
          {aggregations.map((agg, index) => (
            <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <select
                value={agg.function}
                onChange={(e) => updateAggregation(index, { function: e.target.value as any })}
                className="px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
              >
                <option value="count">Count</option>
                <option value="sum">Sum</option>
                <option value="avg">Average</option>
                <option value="min">Min</option>
                <option value="max">Max</option>
              </select>

              <select
                value={agg.field}
                onChange={(e) => updateAggregation(index, { field: e.target.value })}
                className="flex-1 px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
              >
                <option value="id">Record Count</option>
                {numericFields.map(field => (
                  <option key={field.fieldName} value={field.fieldName}>
                    {field.fieldLabel}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={agg.label}
                onChange={(e) => updateAggregation(index, { label: e.target.value })}
                placeholder="Label"
                className="flex-1 px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
              />

              <button
                onClick={() => removeAggregation(index)}
                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addAggregation}
          className="flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Aggregation
        </button>
      </div>
    </div>
  );
};

/**
 * Step 5: Sort Configuration
 */
const SortStep: React.FC<{
  sortBy: ReportSort[];
  onUpdateSortBy: (sorts: ReportSort[]) => void;
  availableFields: any[];
}> = ({ sortBy, onUpdateSortBy, availableFields }) => {
  const addSort = () => {
    onUpdateSortBy([
      ...sortBy,
      { field: availableFields[0]?.fieldName || '', direction: 'asc' }
    ]);
  };

  const updateSort = (index: number, updates: Partial<ReportSort>) => {
    const updated = [...sortBy];
    updated[index] = { ...updated[index], ...updates };
    onUpdateSortBy(updated);
  };

  const removeSort = (index: number) => {
    onUpdateSortBy(sortBy.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Sort order (optional)
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Define how records should be sorted in your report
        </p>
      </div>

      <div className="space-y-3">
        {sortBy.map((sort, index) => (
          <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <select
              value={sort.field}
              onChange={(e) => updateSort(index, { field: e.target.value })}
              className="flex-1 px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
            >
              {availableFields.map(field => (
                <option key={field.fieldName} value={field.fieldName}>
                  {field.fieldLabel}
                </option>
              ))}
            </select>

            <select
              value={sort.direction}
              onChange={(e) => updateSort(index, { direction: e.target.value as 'asc' | 'desc' })}
              className="px-3 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
            >
              <option value="asc">Ascending (A-Z)</option>
              <option value="desc">Descending (Z-A)</option>
            </select>

            <button
              onClick={() => removeSort(index)}
              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={addSort}
        disabled={availableFields.length === 0}
        className="flex items-center gap-2 px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors disabled:opacity-50"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Sort
      </button>

      {sortBy.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p>No sort order defined</p>
          <p className="text-sm mt-1">Records will appear in default order</p>
        </div>
      )}
    </div>
  );
};

/**
 * Step 6: Chart Type Selection
 */
const ChartTypeStep: React.FC<{
  chartType: 'table' | 'bar' | 'line' | 'pie' | 'summary';
  onSelectChartType: (type: 'table' | 'bar' | 'line' | 'pie' | 'summary') => void;
}> = ({ chartType, onSelectChartType }) => {
  const chartTypes = [
    {
      id: 'table' as const,
      name: 'Table',
      icon: '📊',
      description: 'Display data in rows and columns'
    },
    {
      id: 'bar' as const,
      name: 'Bar Chart',
      icon: '📊',
      description: 'Compare values across categories'
    },
    {
      id: 'line' as const,
      name: 'Line Chart',
      icon: '📈',
      description: 'Show trends over time'
    },
    {
      id: 'pie' as const,
      name: 'Pie Chart',
      icon: '🥧',
      description: 'Show proportions and percentages'
    },
    {
      id: 'summary' as const,
      name: 'Summary',
      icon: '📋',
      description: 'Show key metrics and totals'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Choose chart type
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Select how you want to visualize your report data
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {chartTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => onSelectChartType(type.id)}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              chartType === type.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{type.icon}</span>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{type.description}</div>
              </div>
              {chartType === type.id && (
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Step 7: Preview & Save
 */
const PreviewStep: React.FC<{
  reportName: string;
  onUpdateReportName: (name: string) => void;
  previewResults: any;
  isLoading: boolean;
  onRefresh: () => void;
}> = ({ reportName, onUpdateReportName, previewResults, isLoading, onRefresh }) => {
  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Preview and save
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Review your report configuration and give it a name
        </p>
      </div>

      {/* Report Name */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Report Name *
        </label>
        <input
          type="text"
          value={reportName}
          onChange={(e) => onUpdateReportName(e.target.value)}
          placeholder="e.g., Monthly Sales Report"
          className="w-full px-4 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Preview Results */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900 dark:text-white">Report Preview</h4>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Generating preview...</p>
          </div>
        ) : previewResults ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong>{previewResults.totalRecords}</strong> records will be included in this report
            </div>
            {previewResults.summary && (
              <div className="bg-white dark:bg-[#1e293b] rounded p-3">
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">Summary</div>
                {Object.entries(previewResults.summary).map(([key, value]: [string, any]) => (
                  <div key={key} className="flex justify-between text-sm py-1">
                    <span className="text-gray-600 dark:text-gray-400">{value.label}:</span>
                    <span className="text-gray-900 dark:text-white font-medium">{value.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p>No preview available</p>
            <p className="text-sm mt-1">Click "Refresh Preview" to generate</p>
          </div>
        )}
      </div>
    </div>
  );
};
